/**
 * Panel search state — session-scoped, per-scope, never persisted.
 *
 * The library and deck panels keep three pieces of search state alive across
 * panel close/reopen and tab switches, without leaking one scope's words into
 * another: which mode (keyword vs AI) the input is in, the committed keyword
 * query (library only — the deck's committed keyword is `ui$.cardSearchKeyword`,
 * shared with the card list), and the half-typed AI draft.
 *
 * Deliberately a plain observable with no `syncObservable`: a page refresh or a
 * logout (full reload to /login) drops it, which is the whole contract — search
 * words must not survive a refresh or a login session. The scope key format is
 * stable and readable so tests can address scopes directly: 'all' | 'inbox' |
 * 'list:<id>' | 'deck:<id>'.
 */

import { observable } from '@legendapp/state';
import { SEARCH_MODES } from '../constants/searchModes';

export const panelSearch$ = observable({});

const DEFAULT_ENTRY = Object.freeze({
  mode: SEARCH_MODES.AI,
  keyword: '',
  keywordInput: '',
  draft: '',
});

export const getPanelSearchScopeKey = ({ scopeType, scopeId }) => (
  scopeId ? `${scopeType}:${scopeId}` : scopeType
);

const ensureEntry = (scopeKey) => {
  if (panelSearch$[scopeKey].peek() == null) {
    panelSearch$[scopeKey].set({ ...DEFAULT_ENTRY });
  }
};

export const setPanelSearchMode = (scopeKey, mode) => {
  ensureEntry(scopeKey);
  panelSearch$[scopeKey].mode.set(mode);
};

export const setPanelSearchKeyword = (scopeKey, keyword) => {
  ensureEntry(scopeKey);
  panelSearch$[scopeKey].keyword.set(keyword);
};

export const setPanelSearchKeywordInput = (scopeKey, keywordInput) => {
  ensureEntry(scopeKey);
  panelSearch$[scopeKey].keywordInput.set(keywordInput);
};

export const setPanelSearchDraft = (scopeKey, draft) => {
  ensureEntry(scopeKey);
  panelSearch$[scopeKey].draft.set(draft);
};

/**
 * Reset one scope back to defaults — used when a task ends for good, e.g.
 * leaving a deck. Other scopes' entries are untouched.
 */
export const clearPanelSearchScope = (scopeKey) => {
  panelSearch$[scopeKey].set({ ...DEFAULT_ENTRY });
};
